import {
    ITCIMenuInput,
    ITCITenantComponentInput,
    ITCITenantComponentOptions,
    ITCITenantComponentOutput,
    TCITenantAppType,
    TCITenantSelectorType
} from "@tci/common";
import { Injector, Input, Output } from "@angular/core";
import { Observable } from "rxjs";

// tslint:disable:no-empty-interface
// tslint:disable:component-class-suffix
// tslint:disable:max-line-length
declare var Reflect;

/**
 */
export type DistinctionKeyFunction<T extends ITCITenantComponentInput> = (input: T) => string;
/**
 *
 */
export type CanLoadComponentFunction<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput> =
    (options: O, input: T, injector: Injector) => boolean | Observable<boolean>;

export type InlineMenuItemHandlerFunction<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput> =
    (options: O, input: T, injector: Injector) => Observable<any>;

export const TCI_TENANT_COMPONENT_LOADER_SYMBOL = Symbol("@TCITenantComponentLoader");
export const TCI_TENANT_COMPONENT_SYMBOL = Symbol("@TCITenantComponent");

export interface ITCITenantComponentLoaderMetadata extends ITCITenantComponentInput {
    /**
     * Load component by finding multiple implementations from remote svc endpoint
     * and matching with tenant type and selector
     */
    isdynamic: boolean;
}

/**
 * TCI Tenant component information
 */
export interface ITCITenantComponentMetaData {
    /**
     * TCI tenant app type
     */
    tenantAppType?: TCITenantAppType;

    /**
     * component selector without the html tag delimiters e.g. app-create
     */
    componentSelector?: TCITenantSelectorType;

    /**
     * Key function to remove duplicate Tenant component from the same base class
     * @param input
     * component unique
     */
    distinctionKeyFn?: DistinctionKeyFunction<any>;
    /**
     * Check before loading the component menu
     * @param input
     * @example
     * ```
     *  canLoadComponent: <A extends TCI_MENU_TYPE, B extends ITCITenantComponentOptions<A>, C extends ITCIAppDetailMenuInput>
     * (options: B, menuInput: C): boolean => {
     *   return true;
     * }
     * ```
     * @deprecated moving to tci-tenant-model
     */
    canLoadComponent?: CanLoadComponentFunction<any, any, any>;
}

/**
 * The [[TciTenant]] being the abstract base class is extended  by a tenant provider
 * The Tenant provider provides @TCITenantComponent({}) annotation based on the @ITCITenantComponentMetaData.
 * The annotation describes the tenant type and an array of Function providers
 * Each function provider has its own interface for input and output
 *  ### Example [[TciTenant]]  Typescript file
 *  ```
 *  import {Injectable, Injector} from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import {TciTenant} from "@tci/core";
 *  import {Http} from "@angular/http";
 *  @TCITenantComponent(
 *      isDynamic: false,
 *      tenantAppType: TCI_TENANT_APP_TYPE.flogo,
 *      moduleRoute: "/path/to/the/tenant/module.ts";
 *      moduleClass: "TenantComponent";
 *      componentSelector: "tenant-cmp";
 *   )
 *  @Component({
 *   selector: "tenant-cmp",
 *   moduleId: module.id,
 *   template: "<tenant></tenant>"
 *  })
 * export default class TenantComponent extends AbstractTCITenantComponent {
 *      public onTenantInit() {}
 *      public onTenantDestroy() {}
 * }
 *
 *  ```
 *  This tenant needs to be exposed from the `${tenant-name}-svc` entry point module as
 *  @NgModule({
 *   imports: [
 *       HttpModule
 *   ],
 *   exports: [
 *
 *   ],
 * })
 * export default class TenantModule {
 * }
 * abstract class @AbstractTCIRemoteComponent< Event Type, Input type, Output Type>
 *  This is extended on the Remote Component Side
 */
export class AbstractTCITenantComponent<E, I extends ITCITenantComponentInput, O extends ITCITenantComponentOutput<E>> {
    @Input() public input: I;
    @Output() public output: O;

    /**
     *
     * @param input must be @Injectable()
     * @param output must be @Injectable()
     */
    public constructor() {
    }

    /**
     * get annotation from metadata
     * @protected
     */
    public getAnnotations(): ITCITenantComponentMetaData {
        return <ITCITenantComponentMetaData>Reflect.getMetadata(
            "custom:annotation",
            this.constructor,
            TCI_TENANT_COMPONENT_SYMBOL
        );
    }

    getTenantAppType(): TCITenantAppType {
        const metadata = this.getAnnotations();
        return metadata ? metadata.tenantAppType : null;
    }

    getComponentSelector(): TCITenantSelectorType {
        const metadata = this.getAnnotations();
        return metadata ? metadata.componentSelector : null;
    }

    /**
     * get distinction key function
     * @deprecated Not in use anymore as Component cache key (selector+tenant) makes each
     * component unique
     */
    getDistinctionKeyFunction(): (input: ITCITenantComponentInput) => string {
        const metadata = this.getAnnotations();
        return metadata ? metadata.distinctionKeyFn : null;
    }

    /**
     * get canLoadComponent key function
     */
    getcanLoadComponentFunction(): CanLoadComponentFunction<any, any, any> {
        const metadata = this.getAnnotations();
        return metadata ? metadata.canLoadComponent : null;
    }
}

/**
 * @description This decorator and annotation indicates the typescript class is a TCI component contribution
 * ## Example
 * ```
 *
 *   @TCITenantComponent()
 *   class RestService {}
 * ```
 *
 *
 * @param {WiContribMetaData} metadata
 * @return {(target: Function) => void}
 * @constructor
 */
export function TCITenantComponent(metadata: ITCITenantComponentMetaData): (target: Function) => void {
    return (target: Function): any => {
        // console.log("TCITenantComponent(" + metadata + ") called on: ", target);
        Reflect.defineMetadata("custom:annotation", metadata, target, TCI_TENANT_COMPONENT_SYMBOL);
        return target;
    };
}

/**
 * TODO >>>> UPDATE THIS
 * @description This decorator and annotation indicates the typescript class is a TCI component contribution
 * ## Example
 * ```
 *
 *   @TCITenantComponent()
 *   class RestService {}
 * ```
 *
 *
 * @param {WiContribMetaData} metadata
 * @return {(target: Function) => void}
 * @constructor
 */
export function TCITenantLoaderComponent(metadata: ITCITenantComponentLoaderMetadata): (target: Function) => void {
    return (target: Function): any => {
        // console.log("TCITenantComponent(" + metadata + ") called on: ", target);
        Reflect.defineMetadata("custom:annotation", metadata, target, TCI_TENANT_COMPONENT_LOADER_SYMBOL);
        return target;
    };
}

